import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  addDoc,
  updateDoc,
  serverTimestamp,
  onSnapshot,
} from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { searchGroups } from '../services/groupService';
import { getUsersByIds } from '../services/userService';
import { approveMatch } from '../services/matchService';
import {
  COLLECTIONS,
  GROUPS_FIELDS,
  GROUP_MEMBERS_FIELDS,
  GROUP_MEMBER_STATUS,
  GROUP_MEMBER_ROLES,
  USERS_FIELDS,
  MATCHES_FIELDS,
  MATCH_STATUS,
  SET_FIELDS,
  MATCH_TEAM_FIELDS,
  PERIODS_FIELDS,
  RATINGS_FIELDS,
} from '../domain/dataModel';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { MATCH_TYPE_LABELS, getMatchTeamSides, matchTypeBadgeClass } from '../utils/matchDisplay';

const GROUP_DOT_COLORS = [
  'bg-rose-500',
  'bg-amber-500',
  'bg-emerald-500',
  'bg-violet-500',
  'bg-cyan-500',
  'bg-orange-500',
];

function groupDotColor(name) {
  const s = name || '';
  let h = 0;
  for (let i = 0; i < s.length; i += 1) h += s.charCodeAt(i);
  return GROUP_DOT_COLORS[h % GROUP_DOT_COLORS.length];
}

function wasUserOnCourt(match, userId) {
  const sets = match[MATCHES_FIELDS.SETS] || [];
  for (const s of sets) {
    const t1 = s[SET_FIELDS.TEAM1] || {};
    const t2 = s[SET_FIELDS.TEAM2] || {};
    const ids = [t1[MATCH_TEAM_FIELDS.PLAYER1], t1[MATCH_TEAM_FIELDS.PLAYER2], t2[MATCH_TEAM_FIELDS.PLAYER1], t2[MATCH_TEAM_FIELDS.PLAYER2]];
    if (ids.some((id) => id === userId)) return true;
  }
  return false;
}

function getRequiredApprovals(match, members) {
  const sets = match[MATCHES_FIELDS.SETS] || [];
  const playerIds = new Set();
  for (const s of sets) {
    const t1 = s[SET_FIELDS.TEAM1] || {};
    const t2 = s[SET_FIELDS.TEAM2] || {};
    [t1[MATCH_TEAM_FIELDS.PLAYER1], t1[MATCH_TEAM_FIELDS.PLAYER2], t2[MATCH_TEAM_FIELDS.PLAYER1], t2[MATCH_TEAM_FIELDS.PLAYER2]].forEach((id) => id && playerIds.add(id));
  }
  let realCount = 0;
  playerIds.forEach((playerId) => {
    const member = members.find((m) => m.id === playerId || m[GROUP_MEMBERS_FIELDS.USER_ID] === playerId);
    if (member && member[GROUP_MEMBERS_FIELDS.USER_ID] != null && !member[GROUP_MEMBERS_FIELDS.IS_GUEST]) realCount++;
  });
  return Math.min(3, realCount);
}

function IconSearch({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <circle cx="11" cy="11" r="8" />
      <path d="m21 21-4.3-4.3" strokeLinecap="round" />
    </svg>
  );
}

function IconPlus({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d="M12 5v14M5 12h14" strokeLinecap="round" />
    </svg>
  );
}

export default function Dashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [userProfile, setUserProfile] = useState(null);
  const [memberships, setMemberships] = useState([]);
  const [groupsMap, setGroupsMap] = useState({});
  const [ownersMap, setOwnersMap] = useState({});
  const [activeCountMap, setActiveCountMap] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [latestMatches, setLatestMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  const [createLoading, setCreateLoading] = useState(false);
  const [createError, setCreateError] = useState('');
  const [requestLoadingId, setRequestLoadingId] = useState(null);
  const [acceptRejectLoadingId, setAcceptRejectLoadingId] = useState(null);
  const [pendingMatchesByGroupId, setPendingMatchesByGroupId] = useState({});
  const [membersByGroupId, setMembersByGroupId] = useState({});
  const [approveLoadingId, setApproveLoadingId] = useState(null);
  const [approverDisplayNames, setApproverDisplayNames] = useState({});

  const myGroupIds = useMemo(
    () => memberships.filter((m) => m[GROUP_MEMBERS_FIELDS.STATUS] === GROUP_MEMBER_STATUS.ACTIVE).map((m) => m[GROUP_MEMBERS_FIELDS.GROUP_ID]),
    [memberships]
  );
  const myGroupIdsKey = useMemo(() => myGroupIds.join(','), [myGroupIds]);
  const myGroups = useMemo(
    () => myGroupIds.map((id) => groupsMap[id]).filter(Boolean),
    [myGroupIds, groupsMap]
  );
  const invites = useMemo(
    () => memberships.filter((m) => m[GROUP_MEMBERS_FIELDS.STATUS] === GROUP_MEMBER_STATUS.INVITED),
    [memberships]
  );
  const pendingRequests = useMemo(
    () => memberships.filter((m) => m[GROUP_MEMBERS_FIELDS.STATUS] === GROUP_MEMBER_STATUS.PENDING_REQUEST),
    [memberships]
  );
  const membershipByGroupId = useMemo(() => {
    const map = {};
    memberships.forEach((m) => {
      map[m[GROUP_MEMBERS_FIELDS.GROUP_ID]] = m;
    });
    return map;
  }, [memberships]);

  const toApproveMatches = useMemo(() => {
    const list = [];
    myGroupIds.forEach((groupId) => {
      const group = groupsMap[groupId];
      const matches = pendingMatchesByGroupId[groupId] || [];
      const groupMembers = membersByGroupId[groupId] || [];
      const isOwner = group && user?.uid && group[GROUPS_FIELDS.OWNER_ID] === user.uid;
      matches.forEach((m) => {
        const approvals = m[MATCHES_FIELDS.APPROVALS] || [];
        const hasAlreadyApproved = user?.uid && approvals.some((a) => a.userId === user.uid);
        const wasInMatch = user?.uid ? wasUserOnCourt(m, user.uid) : false;
        const isGroupOwner = !!isOwner;
        const canApprove = user?.uid && (wasInMatch || isGroupOwner) && !hasAlreadyApproved;
        if (canApprove) list.push({ match: m, groupId, groupName: group?.[GROUPS_FIELDS.NAME] || groupId, isOwner, requiredApprovals: getRequiredApprovals(m, groupMembers) });
      });
    });
    return list;
  }, [myGroupIds, groupsMap, pendingMatchesByGroupId, membersByGroupId, user?.uid]);

  const approverUserIds = useMemo(() => {
    const ids = new Set();
    toApproveMatches.forEach(({ match }) =>
      (match[MATCHES_FIELDS.APPROVALS] || []).forEach((a) => a.userId && ids.add(a.userId))
    );
    return [...ids];
  }, [toApproveMatches]);
  const approverUserIdsKey = useMemo(() => approverUserIds.join(','), [approverUserIds]);

  useEffect(() => {
    if (!user?.uid) return;
    let cancelled = false;

    async function load() {
      setLoading(true);
      setError(null);
      try {
        const [profileSnap, membersSnap] = await Promise.all([
          getDoc(doc(db, COLLECTIONS.USERS, user.uid)),
          getDocs(query(collection(db, COLLECTIONS.GROUP_MEMBERS), where(GROUP_MEMBERS_FIELDS.USER_ID, '==', user.uid))),
        ]);
        if (cancelled) return;

        const profile = profileSnap.exists() ? profileSnap.data() : null;
        setUserProfile(profile);

        const list = membersSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setMemberships(list);

        const gIds = [...new Set(list.map((m) => m[GROUP_MEMBERS_FIELDS.GROUP_ID]))];
        if (gIds.length === 0) {
          setGroupsMap({});
          setOwnersMap({});
          setActiveCountMap({});
          setLoading(false);
          return;
        }

        const groupSnaps = await Promise.all(gIds.map((id) => getDoc(doc(db, COLLECTIONS.GROUPS, id))));
        if (cancelled) return;
        const gMap = {};
        const ownerIds = new Set();
        groupSnaps.forEach((snap, i) => {
          if (snap.exists()) {
            gMap[gIds[i]] = { id: snap.id, ...snap.data() };
            const oid = snap.data()[GROUPS_FIELDS.OWNER_ID];
            if (oid != null && oid !== '') ownerIds.add(oid);
          }
        });
        setGroupsMap(gMap);

        const ownerIdsArr = [...ownerIds];
        const ownersData = await getUsersByIds(ownerIdsArr);
        if (cancelled) return;
        const oMap = {};
        ownerIdsArr.forEach((uid) => {
          const u = ownersData[uid];
          oMap[uid] = u ? (u[USERS_FIELDS.DISPLAY_NAME] || u[USERS_FIELDS.EMAIL] || uid) : uid;
        });
        setOwnersMap(oMap);

        const countPromises = gIds.map(async (groupId) => {
          const snap = await getDocs(
            query(
              collection(db, COLLECTIONS.GROUP_MEMBERS),
              where(GROUP_MEMBERS_FIELDS.GROUP_ID, '==', groupId),
              where(GROUP_MEMBERS_FIELDS.STATUS, '==', GROUP_MEMBER_STATUS.ACTIVE)
            )
          );
          return [groupId, snap.size];
        });
        const counts = await Promise.all(countPromises);
        if (cancelled) return;
        setActiveCountMap(Object.fromEntries(counts));
      } catch (err) {
        if (!cancelled) setError(err.message || 'Errore nel caricamento.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [user?.uid]);

  useEffect(() => {
    if (!user?.uid || myGroupIds.length === 0) {
      setLatestMatches([]);
      return;
    }
    let cancelled = false;

    async function loadMatches() {
      const matches = [];
      for (const groupId of myGroupIds) {
        const q = query(
          collection(db, COLLECTIONS.MATCHES),
          where(MATCHES_FIELDS.GROUP_ID, '==', groupId)
        );
        try {
          const snap = await getDocs(q);
          if (cancelled) return;
          snap.docs.forEach((d) => {
            const data = d.data();
            const sets = data[MATCHES_FIELDS.SETS] || [];
            const playerIds = new Set();
            sets.forEach((s) => {
              const t1 = s[SET_FIELDS.TEAM1] || {};
              const t2 = s[SET_FIELDS.TEAM2] || {};
              [t1[MATCH_TEAM_FIELDS.PLAYER1], t1[MATCH_TEAM_FIELDS.PLAYER2], t2[MATCH_TEAM_FIELDS.PLAYER1], t2[MATCH_TEAM_FIELDS.PLAYER2]].forEach((id) => id && playerIds.add(id));
            });
            if (playerIds.has(user.uid)) {
              matches.push({ id: d.id, groupId, ...data });
            }
          });
        } catch (_) {
          if (!cancelled) setLatestMatches([]);
          return;
        }
      }
      matches.sort((a, b) => {
        const tA = a[MATCHES_FIELDS.PLAYED_AT] || a[MATCHES_FIELDS.CREATED_AT];
        const tB = b[MATCHES_FIELDS.PLAYED_AT] || b[MATCHES_FIELDS.CREATED_AT];
        if (!tA || !tB) return 0;
        return (tB.toMillis?.() ?? 0) - (tA.toMillis?.() ?? 0);
      });
      if (!cancelled) setLatestMatches(matches.slice(0, 3));
    }
    loadMatches();
    return () => { cancelled = true; };
  }, [user?.uid, myGroupIdsKey, myGroupIds]);

  // Real-time: partite in attesa di approvazione per ogni mio gruppo
  useEffect(() => {
    if (!user?.uid || myGroupIds.length === 0) {
      setPendingMatchesByGroupId({});
      return;
    }
    const unsubs = myGroupIds.map((groupId) => {
      const q = query(
        collection(db, COLLECTIONS.MATCHES),
        where(MATCHES_FIELDS.GROUP_ID, '==', groupId),
        where(MATCHES_FIELDS.STATUS, '==', MATCH_STATUS.PENDING_APPROVAL)
      );
      return onSnapshot(q, (snap) => {
        const docs = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setPendingMatchesByGroupId((prev) => ({ ...prev, [groupId]: docs }));
      }, () => setPendingMatchesByGroupId((prev) => ({ ...prev, [groupId]: [] })));
    });
    return () => unsubs.forEach((u) => u());
  }, [user?.uid, myGroupIdsKey, myGroupIds]);

  // Carica membri per ogni gruppo (per calcolare requiredApprovals)
  useEffect(() => {
    if (!user?.uid || myGroupIds.length === 0) {
      setMembersByGroupId({});
      return;
    }
    let cancelled = false;
    (async () => {
      const map = {};
      await Promise.all(
        myGroupIds.map(async (groupId) => {
          const snap = await getDocs(
            query(
              collection(db, COLLECTIONS.GROUP_MEMBERS),
              where(GROUP_MEMBERS_FIELDS.GROUP_ID, '==', groupId),
              where(GROUP_MEMBERS_FIELDS.STATUS, '==', GROUP_MEMBER_STATUS.ACTIVE)
            )
          );
          if (!cancelled) map[groupId] = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        })
      );
      if (!cancelled) setMembersByGroupId(map);
    })();
    return () => { cancelled = true; };
  }, [user?.uid, myGroupIdsKey, myGroupIds]);

  // Carica display name degli approvatori
  useEffect(() => {
    if (approverUserIds.length === 0) {
      setApproverDisplayNames({});
      return;
    }
    let cancelled = false;
    (async () => {
      const usersData = await getUsersByIds(approverUserIds);
      if (cancelled) return;
      const map = {};
      approverUserIds.forEach((uid) => {
        const u = usersData[uid];
        map[uid] = u ? (u[USERS_FIELDS.DISPLAY_NAME] || u[USERS_FIELDS.EMAIL] || uid) : uid;
      });
      setApproverDisplayNames(map);
    })();
    return () => { cancelled = true; };
  }, [approverUserIdsKey, approverUserIds]);

  const handleSearch = async () => {
    const term = searchTerm.trim();
    if (!term) {
      setSearchResults([]);
      return;
    }
    setSearchLoading(true);
    try {
      const list = await searchGroups(term);
      const ownerIds = [...new Set(list.map((g) => g[GROUPS_FIELDS.OWNER_ID]).filter((id) => id != null && id !== ''))];
      const ownersData = await getUsersByIds(ownerIds);
      const oMap = {};
      ownerIds.forEach((uid) => {
        const u = ownersData[uid];
        oMap[uid] = u ? (u[USERS_FIELDS.DISPLAY_NAME] || u[USERS_FIELDS.EMAIL] || uid) : uid;
      });
      const countPromises = list.map(async (g) => {
        const c = await getDocs(
          query(
            collection(db, COLLECTIONS.GROUP_MEMBERS),
            where(GROUP_MEMBERS_FIELDS.GROUP_ID, '==', g.id),
            where(GROUP_MEMBERS_FIELDS.STATUS, '==', GROUP_MEMBER_STATUS.ACTIVE)
          )
        );
        return [g.id, c.size];
      });
      const counts = await Promise.all(countPromises);
      setSearchResults(
        list.map((g) => ({
          ...g,
          ownerName: g[GROUPS_FIELDS.OWNER_ID] != null && g[GROUPS_FIELDS.OWNER_ID] !== '' ? (oMap[g[GROUPS_FIELDS.OWNER_ID]] || g[GROUPS_FIELDS.OWNER_ID]) : 'Nessun proprietario',
          activeCount: Object.fromEntries(counts)[g.id] ?? 0,
        }))
      );
    } catch (err) {
      setSearchResults([]);
      setError(err.message || 'Errore nella ricerca.');
    } finally {
      setSearchLoading(false);
    }
  };

  const handleApproveMatch = async (match) => {
    setApproveLoadingId(match.id);
    setError(null);
    try {
      await approveMatch(match.id, user.uid, membersByGroupId[match[MATCHES_FIELDS.GROUP_ID]] || []);
    } catch (err) {
      setError(err.message || 'Errore durante l\'approvazione');
    } finally {
      setApproveLoadingId(null);
    }
  };

  const handleRequestJoin = async (groupId) => {
    setRequestLoadingId(groupId);
    setError(null);
    try {
      const ref = await addDoc(collection(db, COLLECTIONS.GROUP_MEMBERS), {
        [GROUP_MEMBERS_FIELDS.GROUP_ID]: groupId,
        [GROUP_MEMBERS_FIELDS.USER_ID]: user.uid,
        [GROUP_MEMBERS_FIELDS.STATUS]: GROUP_MEMBER_STATUS.PENDING_REQUEST,
        [GROUP_MEMBERS_FIELDS.UPDATED_AT]: serverTimestamp(),
      });
      setMemberships((prev) => [
        ...prev,
        {
          id: ref.id,
          [GROUP_MEMBERS_FIELDS.GROUP_ID]: groupId,
          [GROUP_MEMBERS_FIELDS.USER_ID]: user.uid,
          [GROUP_MEMBERS_FIELDS.STATUS]: GROUP_MEMBER_STATUS.PENDING_REQUEST,
        },
      ]);
    } catch (err) {
      setError(err.message || 'Errore nell\'invio della richiesta.');
    } finally {
      setRequestLoadingId(null);
    }
  };

  const handleAcceptInvite = async (membershipId) => {
    setAcceptRejectLoadingId(membershipId);
    setError(null);
    try {
      await updateDoc(doc(db, COLLECTIONS.GROUP_MEMBERS, membershipId), {
        [GROUP_MEMBERS_FIELDS.STATUS]: GROUP_MEMBER_STATUS.ACTIVE,
        [GROUP_MEMBERS_FIELDS.JOINED_AT]: serverTimestamp(),
        [GROUP_MEMBERS_FIELDS.UPDATED_AT]: serverTimestamp(),
      });
      const membership = memberships.find((m) => m.id === membershipId);
      if (membership && user?.uid) {
        const groupId = membership[GROUP_MEMBERS_FIELDS.GROUP_ID];
        const periodsSnap = await getDocs(query(collection(db, COLLECTIONS.PERIODS), where(PERIODS_FIELDS.GROUP_ID, '==', groupId)));
        const now = new Date();
        const activePeriod = periodsSnap.docs.find((d) => {
          const data = d.data();
          const startRaw = data[PERIODS_FIELDS.START_DATE];
          const endRaw = data[PERIODS_FIELDS.END_DATE];
          if (!startRaw || !endRaw) return false;
          const startDate = startRaw.toDate ? startRaw.toDate() : new Date(startRaw);
          const endDate = endRaw.toDate ? endRaw.toDate() : new Date(endRaw);
          return startDate <= now && now <= endDate;
        });
        if (activePeriod) {
          const periodId = activePeriod.id;
          const ratingsSnap = await getDocs(query(
            collection(db, COLLECTIONS.RATINGS),
            where(RATINGS_FIELDS.GROUP_ID, '==', groupId),
            where(RATINGS_FIELDS.PERIOD_ID, '==', periodId),
            where(RATINGS_FIELDS.USER_ID, '==', user.uid)
          ));
          if (ratingsSnap.docs.length > 0) {
            await Promise.all(ratingsSnap.docs.map((d) =>
              updateDoc(d.ref, { [RATINGS_FIELDS.IS_ACTIVE]: true, [RATINGS_FIELDS.UPDATED_AT]: serverTimestamp() })
            ));
          } else {
            await addDoc(collection(db, COLLECTIONS.RATINGS), {
              [RATINGS_FIELDS.GROUP_ID]: groupId,
              [RATINGS_FIELDS.PERIOD_ID]: periodId,
              [RATINGS_FIELDS.USER_ID]: user.uid,
              [RATINGS_FIELDS.POINTS]: 1000,
              [RATINGS_FIELDS.MATCHES_PLAYED]: 0,
              [RATINGS_FIELDS.WINS]: 0,
              [RATINGS_FIELDS.LOSSES]: 0,
              [RATINGS_FIELDS.IS_ACTIVE]: true,
              gamesWon: 0,
              winRate: 0,
              [RATINGS_FIELDS.UPDATED_AT]: serverTimestamp(),
            });
          }
        }
      }
      setMemberships((prev) =>
        prev.map((m) => (m.id === membershipId ? { ...m, [GROUP_MEMBERS_FIELDS.STATUS]: GROUP_MEMBER_STATUS.ACTIVE } : m))
      );
    } catch (err) {
      setError(err.message || 'Errore nell\'accettazione.');
    } finally {
      setAcceptRejectLoadingId(null);
    }
  };

  const handleRejectInvite = async (membershipId) => {
    setAcceptRejectLoadingId(membershipId);
    setError(null);
    try {
      await updateDoc(doc(db, COLLECTIONS.GROUP_MEMBERS, membershipId), {
        [GROUP_MEMBERS_FIELDS.STATUS]: GROUP_MEMBER_STATUS.REJECTED,
        [GROUP_MEMBERS_FIELDS.UPDATED_AT]: serverTimestamp(),
      });
      setMemberships((prev) =>
        prev.map((m) => (m.id === membershipId ? { ...m, [GROUP_MEMBERS_FIELDS.STATUS]: GROUP_MEMBER_STATUS.REJECTED } : m))
      );
    } catch (err) {
      setError(err.message || 'Errore nel rifiuto.');
    } finally {
      setAcceptRejectLoadingId(null);
    }
  };

  const handleCancelPendingRequest = async (membershipId) => {
    setAcceptRejectLoadingId(membershipId);
    setError(null);
    try {
      await updateDoc(doc(db, COLLECTIONS.GROUP_MEMBERS, membershipId), {
        [GROUP_MEMBERS_FIELDS.STATUS]: GROUP_MEMBER_STATUS.REJECTED,
        [GROUP_MEMBERS_FIELDS.UPDATED_AT]: serverTimestamp(),
      });
      setMemberships((prev) =>
        prev.map((m) => (m.id === membershipId ? { ...m, [GROUP_MEMBERS_FIELDS.STATUS]: GROUP_MEMBER_STATUS.REJECTED } : m))
      );
    } catch (err) {
      setError(err.message || 'Errore nell\'annullamento della richiesta.');
    } finally {
      setAcceptRejectLoadingId(null);
    }
  };

  const handleCreateGroup = async () => {
    const name = newGroupName.trim();
    if (!name) {
      setCreateError('Inserisci un nome per il gruppo.');
      return;
    }
    setCreateLoading(true);
    setCreateError('');
    try {
      const existing = await getDocs(
        query(collection(db, COLLECTIONS.GROUPS), where(GROUPS_FIELDS.NAME, '==', name))
      );
      if (!existing.empty) {
        setCreateError('Esiste già un gruppo con questo nome.');
        setCreateLoading(false);
        return;
      }
      const groupRef = await addDoc(collection(db, COLLECTIONS.GROUPS), {
        [GROUPS_FIELDS.NAME]: name,
        [GROUPS_FIELDS.NAME_LOWER]: name.toLowerCase(),
        [GROUPS_FIELDS.OWNER_ID]: user.uid,
        [GROUPS_FIELDS.CREATED_AT]: serverTimestamp(),
        [GROUPS_FIELDS.UPDATED_AT]: serverTimestamp(),
      });
      await addDoc(collection(db, COLLECTIONS.GROUP_MEMBERS), {
        [GROUP_MEMBERS_FIELDS.GROUP_ID]: groupRef.id,
        [GROUP_MEMBERS_FIELDS.USER_ID]: user.uid,
        [GROUP_MEMBERS_FIELDS.ROLE]: GROUP_MEMBER_ROLES.OWNER,
        [GROUP_MEMBERS_FIELDS.STATUS]: GROUP_MEMBER_STATUS.ACTIVE,
        [GROUP_MEMBERS_FIELDS.JOINED_AT]: serverTimestamp(),
        [GROUP_MEMBERS_FIELDS.UPDATED_AT]: serverTimestamp(),
      });
      setGroupsMap((prev) => ({
        ...prev,
        [groupRef.id]: {
          id: groupRef.id,
          [GROUPS_FIELDS.NAME]: name,
          [GROUPS_FIELDS.OWNER_ID]: user.uid,
        },
      }));
      setOwnersMap((prev) => ({ ...prev, [user.uid]: userProfile?.[USERS_FIELDS.DISPLAY_NAME] || user.email }));
      setActiveCountMap((prev) => ({ ...prev, [groupRef.id]: 1 }));
      setMemberships((prev) => [
        ...prev,
        {
          id: null,
          [GROUP_MEMBERS_FIELDS.GROUP_ID]: groupRef.id,
          [GROUP_MEMBERS_FIELDS.USER_ID]: user.uid,
          [GROUP_MEMBERS_FIELDS.STATUS]: GROUP_MEMBER_STATUS.ACTIVE,
          [GROUP_MEMBERS_FIELDS.ROLE]: GROUP_MEMBER_ROLES.OWNER,
        },
      ]);
      setCreateDialogOpen(false);
      setNewGroupName('');
      navigate(`/groups/${groupRef.id}`);
    } catch (err) {
      setCreateError(err.message || 'Errore nella creazione del gruppo.');
    } finally {
      setCreateLoading(false);
    }
  };

  const displayName = userProfile?.[USERS_FIELDS.DISPLAY_NAME]?.trim() || user?.email || 'Utente';

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-brand-royal border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {error && (
        <div className="rounded-btn border border-loss/60 bg-loss/15 px-4 py-3 text-sm text-red-100">
          {error}
        </div>
      )}

      {/* 1. Intestazione */}
      <div>
        <p className="text-xs font-medium uppercase tracking-widest text-brand-ice/50">Bentornato</p>
        <h1 className="font-display text-2xl font-bold text-brand-ice">{displayName}</h1>
      </div>

      {/* 2. Cerca gruppo */}
      <section className="space-y-2">
        <h2 className="font-display text-lg font-semibold text-brand-ice">Cerca gruppo</h2>
        <div className="flex flex-col gap-2 sm:flex-row sm:items-stretch">
          <div className="relative min-w-0 flex-1">
            <IconSearch className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-brand-ice/50" />
            <Input
              className="pl-9"
              placeholder="Cerca per nome gruppo..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            />
          </div>
          <Button type="button" className="w-full shrink-0 sm:w-auto" onClick={handleSearch} disabled={searchLoading}>
            {searchLoading ? 'Ricerca...' : 'Cerca'}
          </Button>
        </div>
        {searchResults.length > 0 && (
          <ul className="space-y-2">
            {searchResults.map((g) => {
              const mem = membershipByGroupId[g.id];
              const status = mem?.[GROUP_MEMBERS_FIELDS.STATUS];
              const isMember = status === GROUP_MEMBER_STATUS.ACTIVE;
              const isPending = status === GROUP_MEMBER_STATUS.PENDING_REQUEST;
              return (
                <li key={g.id}>
                  <Card className="border-surface-muted bg-surface-card text-left">
                    <CardContent className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between">
                      <div className="min-w-0 space-y-1">
                        <p className="font-display text-base font-semibold text-brand-ice">{g[GROUPS_FIELDS.NAME]}</p>
                        <p className="text-sm text-brand-ice/70">
                          Proprietario: {g.ownerName || '—'} · {g.activeCount ?? 0} membri attivi
                        </p>
                      </div>
                      <div className="flex shrink-0 flex-wrap items-center gap-2">
                        {isMember && <Badge variant="win">Già membro</Badge>}
                        {isPending && <Badge variant="pending">Richiesta inviata</Badge>}
                        {!isMember && !isPending && (
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => handleRequestJoin(g.id)}
                            disabled={requestLoadingId === g.id}
                          >
                            {requestLoadingId === g.id ? 'Invio...' : 'Richiedi di entrare'}
                          </Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      {/* 3. I miei gruppi */}
      <section className="space-y-2">
        <h2 className="font-display text-lg font-semibold text-brand-ice">I miei gruppi</h2>
        {myGroups.length === 0 ? (
          <button
            type="button"
            onClick={() => setCreateDialogOpen(true)}
            className="flex w-full min-h-[100px] flex-col items-center justify-center gap-2 rounded-card border-2 border-dashed border-brand-sky/40 bg-transparent px-4 py-4 text-brand-sky transition-colors hover:border-brand-sky/70 hover:bg-brand-sky/5"
          >
            <IconPlus className="h-6 w-6" />
            <span className="font-ui text-sm font-medium">Crea nuovo gruppo</span>
          </button>
        ) : myGroups.length === 1 ? (
          <div className="space-y-3">
            {myGroups.map((group) => {
              const name = group[GROUPS_FIELDS.NAME];
              const letter = (name && name[0]?.toUpperCase()) || '?';
              const isOwner = group[GROUPS_FIELDS.OWNER_ID] === user.uid;
              return (
                <button
                  key={group.id}
                  type="button"
                  onClick={() => navigate(`/groups/${group.id}`)}
                  className="flex w-full items-start gap-4 rounded-card border border-surface-muted bg-surface-card p-4 text-left transition-colors hover:border-brand-sky/30"
                >
                  <div className={`flex h-14 w-14 shrink-0 items-center justify-center rounded-full text-xl font-bold text-white ${groupDotColor(name)}`}>
                    {letter}
                  </div>
                  <div className="min-w-0 flex-1 space-y-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-display text-lg font-semibold text-brand-ice">{name}</span>
                      {isOwner && <Badge variant="default">Owner</Badge>}
                    </div>
                    <p className="text-sm text-brand-ice/70">{activeCountMap[group.id] ?? 0} membri attivi</p>
                    <p className="text-xs text-brand-ice/50">Tocca per aprire il gruppo</p>
                  </div>
                </button>
              );
            })}
            <button
              type="button"
              onClick={() => setCreateDialogOpen(true)}
              className="flex w-full min-h-[100px] flex-col items-center justify-center gap-2 rounded-card border-2 border-dashed border-brand-sky/40 bg-transparent px-4 py-4 text-brand-sky transition-colors hover:border-brand-sky/70 hover:bg-brand-sky/5"
            >
              <IconPlus className="h-6 w-6" />
              <span className="font-ui text-sm font-medium">Crea nuovo gruppo</span>
            </button>
          </div>
        ) : (
          <div className="-mx-1 flex gap-3 overflow-x-auto pb-2 pl-1 pr-1 pt-1">
            {myGroups.map((group) => {
              const name = group[GROUPS_FIELDS.NAME];
              const letter = (name && name[0]?.toUpperCase()) || '?';
              const isOwner = group[GROUPS_FIELDS.OWNER_ID] === user.uid;
              return (
                <button
                  key={group.id}
                  type="button"
                  onClick={() => navigate(`/groups/${group.id}`)}
                  className="flex w-[140px] min-w-[140px] shrink-0 flex-col items-center gap-2 rounded-card border border-surface-muted bg-surface-card p-3 text-center transition-colors hover:border-brand-sky/30"
                >
                  <div className={`flex h-12 w-12 items-center justify-center rounded-full text-lg font-bold text-white ${groupDotColor(name)}`}>
                    {letter}
                  </div>
                  <span className="line-clamp-2 w-full font-display text-sm font-semibold leading-tight text-brand-ice">{name}</span>
                  <span className="text-xs text-brand-ice/60">{activeCountMap[group.id] ?? 0} attivi</span>
                  {isOwner && <Badge variant="default" className="text-[10px]">Owner</Badge>}
                </button>
              );
            })}
            <button
              type="button"
              onClick={() => setCreateDialogOpen(true)}
              className="flex w-[140px] min-w-[140px] shrink-0 flex-col items-center justify-center gap-2 rounded-card border-2 border-dashed border-brand-sky/40 bg-transparent p-3 text-brand-sky transition-colors hover:border-brand-sky/70 hover:bg-brand-sky/5"
            >
              <IconPlus className="h-7 w-7" />
              <span className="text-center font-ui text-xs font-medium leading-tight">Crea nuovo gruppo</span>
            </button>
          </div>
        )}
      </section>

      {/* 4. Partite da approvare */}
      {toApproveMatches.length > 0 && (
        <section className="space-y-2">
          <h2 className="font-display text-lg font-semibold text-brand-ice">Partite da approvare</h2>
          <Card className="border-brand-royal/30 bg-brand-royal/10">
            <CardContent className="space-y-0 divide-y divide-brand-royal/20 p-0">
              {toApproveMatches.map(({ match, groupName, requiredApprovals }) => {
                const typeKey = match[MATCHES_FIELDS.TYPE] || 'classic';
                const date = match[MATCHES_FIELDS.CREATED_AT] || match[MATCHES_FIELDS.PLAYED_AT];
                const dateStr = date?.toDate?.() ? date.toDate().toLocaleDateString('it-IT') : '—';
                const approvals = match[MATCHES_FIELDS.APPROVALS] || [];
                const count = approvals.length;
                const approving = approveLoadingId === match.id;
                const approverLabel = (uid) => approverDisplayNames[uid] || uid;
                return (
                  <div key={match.id} className="flex flex-col gap-3 p-4 sm:flex-row sm:items-start sm:justify-between">
                    <div className="min-w-0 space-y-2">
                      <p className="font-medium text-brand-ice">
                        {groupName} · {MATCH_TYPE_LABELS[typeKey] || typeKey} · {dateStr}
                      </p>
                      <p className="text-sm text-brand-ice/80">
                        {count}/{requiredApprovals} approvazioni
                      </p>
                      {approvals.length > 0 && (
                        <div className="flex flex-wrap gap-1.5">
                          <span className="text-xs text-brand-ice/60">Approvato da:</span>
                          {approvals.map((a) => (
                            <Badge key={a.userId} variant="win" className="text-[10px]">
                              {approverLabel(a.userId)}
                            </Badge>
                          ))}
                        </div>
                      )}
                    </div>
                    <Button
                      type="button"
                      className="w-full shrink-0 bg-brand-royal hover:bg-brand-navy sm:w-auto"
                      onClick={() => handleApproveMatch(match)}
                      disabled={approving}
                    >
                      Approva
                    </Button>
                  </div>
                );
              })}
            </CardContent>
          </Card>
        </section>
      )}

      {/* 5. Inviti in sospeso */}
      {invites.length > 0 && (
        <section className="space-y-2">
          <h2 className="font-display text-lg font-semibold text-brand-ice">Inviti in sospeso</h2>
          <Card className="border-surface-muted bg-surface-card">
            <CardContent className="space-y-0 divide-y divide-surface-muted p-0">
              {invites.map((inv) => {
                const group = groupsMap[inv[GROUP_MEMBERS_FIELDS.GROUP_ID]];
                const ownerName = group
                  ? (group[GROUPS_FIELDS.OWNER_ID] != null && group[GROUPS_FIELDS.OWNER_ID] !== ''
                    ? (ownersMap[group[GROUPS_FIELDS.OWNER_ID]] ?? group[GROUPS_FIELDS.OWNER_ID])
                    : 'Nessun proprietario')
                  : '—';
                const loadingInv = acceptRejectLoadingId === inv.id;
                return (
                  <div key={inv.id} className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between">
                    <div className="min-w-0">
                      <p className="font-display font-semibold text-brand-ice">{group?.[GROUPS_FIELDS.NAME] || inv[GROUP_MEMBERS_FIELDS.GROUP_ID]}</p>
                      <p className="text-sm text-brand-ice/70">Proprietario: {ownerName}</p>
                    </div>
                    <div className="flex shrink-0 gap-2">
                      <Button type="button" variant="success" size="sm" onClick={() => handleAcceptInvite(inv.id)} disabled={loadingInv}>
                        Accetta
                      </Button>
                      <Button type="button" variant="ghost" size="sm" onClick={() => handleRejectInvite(inv.id)} disabled={loadingInv}>
                        Rifiuta
                      </Button>
                    </div>
                  </div>
                );
              })}
            </CardContent>
          </Card>
        </section>
      )}

      {/* 6. Richieste in sospeso */}
      {pendingRequests.length > 0 && (
        <section className="space-y-2">
          <h2 className="font-display text-lg font-semibold text-brand-ice">Richieste in sospeso</h2>
          <Card className="border-surface-muted bg-surface-card">
            <CardContent className="space-y-0 divide-y divide-surface-muted p-0">
              {pendingRequests.map((req) => {
                const groupId = req[GROUP_MEMBERS_FIELDS.GROUP_ID];
                const group = groupsMap[groupId];
                const groupName = group?.[GROUPS_FIELDS.NAME] || groupId;
                const ownerId = group?.[GROUPS_FIELDS.OWNER_ID];
                const ownerName = ownerId != null && ownerId !== ''
                  ? (ownersMap[ownerId] ?? ownerId)
                  : 'Nessun proprietario';
                const activeCount = activeCountMap[groupId] ?? 0;
                const requestDate = req[GROUP_MEMBERS_FIELDS.UPDATED_AT];
                const requestDateStr = requestDate?.toDate
                  ? requestDate.toDate().toLocaleDateString('it-IT', { day: '2-digit', month: 'long', year: 'numeric' })
                  : '—';
                const loadingReq = acceptRejectLoadingId === req.id;
                return (
                  <div key={req.id || groupId} className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between">
                    <div className="min-w-0 space-y-1">
                      <p className="font-display font-semibold text-brand-ice">{groupName}</p>
                      <p className="text-sm text-brand-ice/70">
                        Proprietario: {ownerName} · {activeCount} membri attivi
                      </p>
                      <p className="text-xs text-brand-ice/60">Richiesta inviata il {requestDateStr}</p>
                      <Badge variant="pending" className="mt-1 w-fit">In attesa</Badge>
                    </div>
                    <Button
                      type="button"
                      variant="destructive"
                      size="sm"
                      className="w-full shrink-0 sm:w-auto"
                      onClick={() => handleCancelPendingRequest(req.id)}
                      disabled={loadingReq}
                    >
                      {loadingReq ? 'Annullamento...' : 'Annulla'}
                    </Button>
                  </div>
                );
              })}
            </CardContent>
          </Card>
        </section>
      )}

      {/* 7. Ultime partite */}
      <section className="space-y-2">
        <h2 className="font-display text-lg font-semibold text-brand-ice">Ultime partite</h2>
        {latestMatches.length === 0 ? (
          <p className="text-sm text-brand-ice/60">Nessuna partita recente.</p>
        ) : (
          <ul className="space-y-3">
            {latestMatches.map((m) => {
              const group = groupsMap[m.groupId];
              const groupName = group?.[GROUPS_FIELDS.NAME] || m.groupId;
              const date = m[MATCHES_FIELDS.PLAYED_AT] || m[MATCHES_FIELDS.CREATED_AT];
              const dateStr = date?.toDate?.() ? date.toDate().toLocaleDateString('it-IT') : '—';
              const score = `${m[MATCHES_FIELDS.SCORE1] ?? 0} - ${m[MATCHES_FIELDS.SCORE2] ?? 0}`;
              const typeKey = m[MATCHES_FIELDS.TYPE] || 'classic';
              const status = m[MATCHES_FIELDS.STATUS];
              const approved = status === MATCH_STATUS.APPROVED;
              const pending = status === MATCH_STATUS.PENDING_APPROVAL;
              const { left, right } = getMatchTeamSides(m, membersByGroupId, user, userProfile);
              return (
                <li key={m.id}>
                  <button
                    type="button"
                    onClick={() => navigate(`/groups/${m.groupId}/matches/${m.id}`)}
                    className="w-full rounded-card border border-surface-muted bg-surface-card p-4 text-left transition-colors hover:border-brand-sky/25"
                  >
                    <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                      <span className={`inline-flex rounded-badge border px-2.5 py-0.5 font-ui text-xs font-semibold ${matchTypeBadgeClass(typeKey)}`}>
                        {MATCH_TYPE_LABELS[typeKey] || typeKey}
                      </span>
                      <span className="text-sm text-brand-ice/70">{dateStr}</span>
                    </div>
                    <div className="mb-3 grid grid-cols-[1fr_auto_1fr] items-center gap-2">
                      <p className="truncate text-left text-xs text-brand-ice/90 sm:text-sm">{left}</p>
                      <p className="font-display text-3xl font-bold tabular-nums text-brand-ice">{score}</p>
                      <p className="truncate text-right text-xs text-brand-ice/90 sm:text-sm">{right}</p>
                    </div>
                    <div className="flex items-center justify-between gap-2 border-t border-surface-muted pt-3 text-xs text-brand-ice/60">
                      <span className="flex items-center gap-1.5">
                        <span
                          className={`h-2 w-2 shrink-0 rounded-full ${approved ? 'bg-win' : pending ? 'bg-amber-500' : 'bg-surface-muted'}`}
                          title={approved ? 'Approvata' : pending ? 'In attesa' : status || ''}
                        />
                        <span>{approved ? 'Approvata' : pending ? 'In attesa di approvazione' : status || '—'}</span>
                      </span>
                      <span className="truncate text-right font-medium text-brand-ice/80">{groupName}</span>
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      {/* 8. Dialog crea gruppo */}
      {createDialogOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4"
          role="presentation"
          onClick={() => !createLoading && setCreateDialogOpen(false)}
        >
          <Card
            className="relative z-10 w-full max-w-md border-surface-muted bg-surface-card shadow-card"
            onClick={(e) => e.stopPropagation()}
          >
            <CardContent className="space-y-4 p-6">
              <h3 className="font-display text-xl font-semibold text-brand-ice">Crea nuovo gruppo</h3>
              {createError && (
                <div className="rounded-btn border border-loss/60 bg-loss/15 px-4 py-3 text-sm text-red-100">
                  {createError}
                </div>
              )}
              <div className="space-y-2">
                <Label htmlFor="dashboard-new-group-name">Nome gruppo</Label>
                <Input
                  id="dashboard-new-group-name"
                  autoFocus
                  value={newGroupName}
                  onChange={(e) => setNewGroupName(e.target.value)}
                  placeholder="Es. Circolo Padel Roma"
                />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button type="button" variant="outline" onClick={() => setCreateDialogOpen(false)} disabled={createLoading}>
                  Annulla
                </Button>
                <Button type="button" onClick={handleCreateGroup} disabled={createLoading}>
                  {createLoading ? 'Creazione...' : 'Crea'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
